import {useCallback, useState} from 'react';
import {useAtomValue} from 'jotai';
import {useSendMessage} from './useSendMessage';
import {useScreenCreationContext} from './ScreenCreationContext';
import {
  isActiveSessionLimit,
  sessionLimitAtom,
} from '@features/subscription/sessionLimitAtom';
import {toast} from 'sonner';
import amplitudeService from '@features/analytics/amplitudeService';
import {captureException} from '@sentry/react';
import {createVariantPrompts} from '@features/ai/prompt/common/variants';
import {trpc} from '@features/utils/trpc';
import type {GenType} from '@features/flow/flowService';

export function useConvertScreenType() {
  const {createScreenRelativeTo} = useScreenCreationContext();
  const {sendMessage} = useSendMessage();
  const [isPending, setIsPending] = useState(false);
  const trpcUtils = trpc.useUtils();
  const sessionLimit = useAtomValue(sessionLimitAtom);

  const convertScreenType = useCallback(
    async (screenId: string, currentGenType: GenType, flowId: string) => {
      setIsPending(true);

      const targetGenType: GenType =
        currentGenType === 'mobile-html-css'
          ? 'web-html-css'
          : 'mobile-html-css';

      const variantAction =
        targetGenType === 'mobile-html-css'
          ? ('convert-to-mobile' as const)
          : ('convert-to-desktop' as const);

      const variantPrompt = createVariantPrompts[variantAction].prompt;

      try {
        const sourceStructure =
          await trpcUtils.screens.getActiveStructure.fetch({
            screenId,
          });
        const screenshotUrl = sourceStructure?.screenshotUrl ?? undefined;

        amplitudeService.logEvent('[Flow] convert screen type', {
          screenId,
          from: currentGenType,
          to: targetGenType,
          hasScreenshot: !!screenshotUrl,
        });

        // Components flows: createScreenRelativeTo resolves to
        // createComponentScreenRelativeTo, which uses the third arg to size
        // the new screen. Without an explicit screenSize the new screen
        // inherits the source's size and the canvas wrapper renders at the
        // wrong dimensions until the LLM finishes writing the JSX file and
        // metadata is re-extracted. For html-css flows the third arg is
        // ignored.
        const newScreenId = await createScreenRelativeTo(screenId, 'right', {
          sourceScreenId: screenId,
          screenSize:
            targetGenType === 'mobile-html-css' ? 'mobile' : 'desktop',
        });

        const label =
          targetGenType === 'mobile-html-css' ? 'mobile' : 'desktop';
        if (isActiveSessionLimit(sessionLimit)) {
          toast('Conversion queued — generates when your session resets');
        } else {
          toast.success(`Converting to ${label}...`);
        }

        await sendMessage({
          screenId: newScreenId,
          messageText: '',
          genType: targetGenType,
          flowId,
          attachments: [
            {
              type: 'createVariant',
              variant: variantAction,
              variantPrompt,
              sourceScreenId: screenId,
              screenshotUrl,
            },
          ],
        });
      } catch (error) {
        captureException(error);
        toast.error('Failed to convert screen type');
      } finally {
        setIsPending(false);
      }
    },
    [createScreenRelativeTo, sendMessage, trpcUtils, sessionLimit],
  );

  return {convertScreenType, isPending};
}
